import { AccordionIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/accordion-icon';
import { CalloutIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/callout-icon';
import { ColumnsIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/columns-icon';
import { KeyIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/key-icon';
import type { SlashMenuItem } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/slash-dropdown-menu/use-slash-dropdown-menu';
import { isNodeInSchema } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-utils';
import type { Editor } from '@tiptap/react';
import { ACCORDION_NODE_NAME, ACCORDION_PANEL_NODE_NAME } from './extensions/accordion';
import { COLUMNS_NODE_NAME, COLUMN_NODE_NAME } from './extensions/columns';
import { CUSTOM_COMPONENT_NODE_NAME } from './extensions/custom-component';
import { KEY_POINT_CALLOUT_NODE_NAME } from './extensions/key-point-callout';
import { SIMPLE_CALLOUT_NODE_NAME } from './extensions/simple-callout';

export const STUDY_NOTES_SLASH_MENU_ITEMS: Record<string, SlashMenuItem> = {
  keypointCallout: {
    metadata: {
      title: 'Key Point',
      subtext: 'Highlighted key point box',
      keywords: ['keypoint', 'key point', 'callout'],
      badge: KeyIcon,
      group: 'Insert',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(KEY_POINT_CALLOUT_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: KEY_POINT_CALLOUT_NODE_NAME,
            attrs: {
              title: 'Key Point',
              color: 'yellow',
              icon: 'key',
            },
            content: [{ type: 'paragraph' }],
          })
          .run();
      },
    },
  },
  simpleCallout: {
    metadata: {
      title: 'Callout',
      subtext: 'Simple callout box with colored background',
      keywords: ['callout', 'simple callout', 'tip', 'example', 'note'],
      badge: CalloutIcon,
      group: 'Insert',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(SIMPLE_CALLOUT_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: SIMPLE_CALLOUT_NODE_NAME,
            attrs: {
              title: 'Callout',
              color: 'sky',
              icon: 'info-circle',
            },
            content: [{ type: 'paragraph' }],
          })
          .run();
      },
    },
  },
  customComponent: {
    metadata: {
      title: 'Custom Component',
      subtext: 'Embed an Angular component with JSON config',
      keywords: ['custom', 'component', 'angular', 'widget', 'interactive'],
      group: 'Insert',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(CUSTOM_COMPONENT_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: CUSTOM_COMPONENT_NODE_NAME,
            attrs: {
              component_id: '',
              inputs: {},
            },
          })
          .run();
      },
    },
  },
  twoColumns: {
    metadata: {
      title: '2 Columns',
      subtext: 'Side-by-side two-column layout',
      keywords: ['columns', '2 columns', 'two columns', 'side by side', 'layout'],
      badge: ColumnsIcon,
      group: 'Columns',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(COLUMNS_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: COLUMNS_NODE_NAME,
            content: [
              {
                type: COLUMN_NODE_NAME,
                content: [{ type: 'paragraph' }],
              },
              {
                type: COLUMN_NODE_NAME,
                content: [{ type: 'paragraph' }],
              },
            ],
          })
          .run();
      },
    },
  },
  threeColumns: {
    metadata: {
      title: '3 Columns',
      subtext: 'Three-column layout',
      keywords: ['columns', '3 columns', 'three columns', 'layout'],
      badge: ColumnsIcon,
      group: 'Columns',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(COLUMNS_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: COLUMNS_NODE_NAME,
            content: [
              {
                type: COLUMN_NODE_NAME,
                content: [{ type: 'paragraph' }],
              },
              {
                type: COLUMN_NODE_NAME,
                content: [{ type: 'paragraph' }],
              },
              {
                type: COLUMN_NODE_NAME,
                content: [{ type: 'paragraph' }],
              },
            ],
          })
          .run();
      },
    },
  },
  accordion: {
    metadata: {
      title: 'Accordion',
      subtext: 'Expandable panel group',
      keywords: ['accordion', 'expansion', 'collapse', 'panels'],
      badge: AccordionIcon,
      group: 'Insert',
    },
    implementation: {
      check: (editor: Editor) => isNodeInSchema(ACCORDION_NODE_NAME, editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: ACCORDION_NODE_NAME,
            content: [
              {
                type: ACCORDION_PANEL_NODE_NAME,
                attrs: { title: 'Panel 1' },
                content: [{ type: 'paragraph' }],
              },
              {
                type: ACCORDION_PANEL_NODE_NAME,
                attrs: { title: 'Panel 2' },
                content: [{ type: 'paragraph' }],
              },
            ],
          })
          .run();
      },
    },
  },
};
